import React from "react"
import { Button, Checkbox, Dropdown, Tooltip } from "antd"
import type { MenuProps } from "antd"
import type { ColumnsType } from "antd/es/table"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faEllipsis,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { describeSignificance } from "shared/ui/Significance/SignificanceControl.component"
import type { ResolvedTable } from "../../model/resolve"
import type { DisplayStatus } from "../../runtime/tabSpecStore"
import type { TableSpec } from "../../types/tabSpecDocument"
import {
  describeBase,
  describeFilterShort,
  describeStatistics,
  isOverridden,
  shownStatistics,
  STATUS_TEXT,
} from "./describe"

/** One line of the grid: a table of the spec with its defaults filled in. */
export interface GridRow {
  id: string
  /** Position in the document. */
  index: number
  /** The table number: its position among the included tables; null when excluded. */
  number: number | null
  /** Whether it is the last table of the document, which cannot move down. */
  last: boolean
  table: TableSpec
  resolved: ResolvedTable
  status: DisplayStatus
  /** The table's findings, then the reason and the warnings of its current run. */
  problems: string[]
  /** The question or variable as the tables show it: its shown label, or its label. */
  shownQuestion: string
  /** Its grid row or loop as the tables show it; null for a table of none. */
  shownInstance: string | null
  /** The Rows cell: the rows in a few words (`describeRows`), each named as shown. */
  rowsText: string
  /** The Type cell: the kind, a grid summary with what it shows (`describeKind`). */
  kindText: string
  /** The assistant added or changed the table since the spec was last saved. */
  ai: boolean
}

/** What the "AI" tag of a table says on hover. */
export const AI_TAG_TITLE =
  "The assistant added or changed this table. Save the spec to keep it, or Undo on the proposal to take it back."

export type RowAction = "up" | "down" | "position" | "duplicate" | "remove"

/**
 * The tone of a status chip (tabSpec.css), as the dashboards' chips: Ready
 * in the accent, Out of date in amber, Failed in red, Running with the
 * pulsing dot, the rest neutral.
 */
const STATUS_TONE: Record<DisplayStatus, "neutral" | "busy" | "good" | "warn" | "bad"> = {
  idle: "neutral",
  queued: "neutral",
  running: "busy",
  done: "good",
  stale: "warn",
  error: "bad",
  skipped: "neutral",
  excluded: "neutral",
}

/**
 * How a row is named to a screen reader: the question, and its grid row or
 * loop, as the tables show them.
 */
export const rowName = (row: GridRow): string => {
  if (row.table.kind === "grid-summary")
    return row.shownInstance
      ? `${row.shownQuestion} - ${row.shownInstance} summary`
      : `${row.shownQuestion} summary`
  return row.shownInstance ? `${row.shownQuestion} - ${row.shownInstance}` : row.shownQuestion
}

/**
 * A value that follows the defaults is muted; one set on the table is not.
 * A cell keeps to one line (tabSpec.css), so a text cut short shows whole
 * as its title.
 */
const Value: React.FC<{ inherited: boolean; children: React.ReactNode }> = ({
  inherited,
  children,
}) => (
  <span
    className={inherited ? "ts-inherited" : undefined}
    title={typeof children === "string" ? children : undefined}
  >
    {children}
  </span>
)

/** The width of antd's tick column (`rowSelection.columnWidth`). */
export const TICK_WIDTH = 32
/**
 * The Question column has no width of its own: it takes what the other
 * columns leave, and never less than this. The other widths are set so the
 * grid fits the 1230 px or so it gets on a 1366 px laptop.
 */
export const QUESTION_MIN_WIDTH = 200

export interface GridColumnArgs {
  /**
   * The project has no questionnaire: every table is of a variable of the
   * data, so the first column reads "Variable" and no row wears "Data".
   */
  dataOnly: boolean
  onInclude: (id: string, include: boolean) => void
  /** Selects the table; the question label is a button, so this works from the keyboard. */
  onSelect: (id: string) => void
  onAction: (row: GridRow, action: RowAction) => void
}

const columns = (args: GridColumnArgs): ColumnsType<GridRow> => [
  {
    title: "#",
    key: "number",
    width: 44,
    render: (_, row) => (
      <span className="ts-number">{row.number ?? ""}</span>
    ),
  },
  {
    title: "Include",
    key: "include",
    width: 68,
    render: (_, row) => (
      <Checkbox
        checked={row.table.include}
        aria-label={`Include ${rowName(row)}`}
        onChange={(e) => args.onInclude(row.id, e.target.checked)}
      />
    ),
  },
  {
    title: args.dataOnly ? "Variable" : "Question",
    key: "question",
    // No width: the rest of the grid, at least QUESTION_MIN_WIDTH.
    render: (_, row) => {
      const { table, resolved } = row
      const text = resolved.question?.text ?? ""
      // Shown as the tables show them; the title keeps the label itself. A
      // grid summary fixed at some values shows them.
      const instance =
        resolved.instance?.label ??
        (table.kind === "grid-summary" ? (row.shownInstance ?? undefined) : undefined)
      return (
        <div className="ts-question">
          <div className="ts-question__head">
            <button
              type="button"
              className="ts-question__label"
              aria-label={rowName(row)}
              title={table.question}
              onClick={() => args.onSelect(row.id)}
            >
              {row.shownQuestion}
            </button>
            {instance && (
              <span className="ts-chip ts-question__instance" title={instance}>
                {row.shownInstance ?? instance}
              </span>
            )}
            {table.source === "data" && !args.dataOnly && (
              <span
                className="ts-chip ts-question__source"
                title="This variable comes from the prepared data, not from the questionnaire."
              >
                Data
              </span>
            )}
            {row.ai && (
              <Tooltip title={AI_TAG_TITLE}>
                <span
                  className="ts-chip"
                  data-tone="good"
                  aria-label={AI_TAG_TITLE}
                >
                  AI
                </span>
              </Tooltip>
            )}
          </div>
          {text && (
            <div className="ts-question__text" title={text}>
              {text}
            </div>
          )}
        </div>
      )
    },
  },
  {
    title: "Type",
    key: "type",
    width: 120,
    render: (_, { kindText }) => (
      <span className="ts-chip" title={kindText}>
        {kindText}
      </span>
    ),
  },
  {
    title: "Banner",
    key: "banner",
    width: 112,
    render: (_, { table, resolved }) => (
      <Value inherited={!isOverridden(table, "bannerId")}>
        {resolved.banner?.name ?? ""}
      </Value>
    ),
  },
  {
    title: "Base",
    key: "base",
    // "All respondents" whole.
    width: 114,
    render: (_, { table, resolved }) => (
      <Value inherited={!isOverridden(table, "base")}>
        {describeBase(resolved)}
      </Value>
    ),
  },
  {
    title: "Rows",
    key: "rows",
    width: 152,
    render: (_, { table, rowsText }) => (
      <Value inherited={!isOverridden(table, "rows") && !isOverridden(table, "values")}>
        {rowsText}
      </Value>
    ),
  },
  {
    title: "Statistics",
    key: "statistics",
    width: 120,
    render: (_, { table, resolved }) => {
      // A text table is never tabulated: it has no statistics to show.
      if (table.kind === "text") return null
      const { fallback } = shownStatistics(resolved)
      return (
        <div className="ts-stats">
          <Value inherited={fallback || !isOverridden(table, "statistics")}>
            {describeStatistics(resolved)}
          </Value>
          <Value inherited={!isOverridden(table, "significance")}>
            {describeSignificance(resolved.significance)}
          </Value>
        </div>
      )
    },
  },
  {
    title: "Filter",
    key: "filter",
    // "2 conditions" whole.
    width: 96,
    render: (_, { table }) => {
      const text = describeFilterShort(table)
      return <span title={text || undefined}>{text}</span>
    },
  },
  {
    title: "Status",
    key: "status",
    width: 120,
    render: (_, row) => (
      <span className="ts-status">
        <span className="ts-chip" data-tone={STATUS_TONE[row.status]}>
          {STATUS_TEXT[row.status]}
        </span>
        {row.problems.length > 0 && (
          <Tooltip
            title={
              // The tooltip renders outside the spec, beyond its stylesheet.
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {row.problems.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            }
          >
            <span
              className="ts-status__warning"
              role="img"
              aria-label={`Things to review for ${rowName(row)}`}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </span>
          </Tooltip>
        )}
      </span>
    ),
  },
  {
    title: "",
    key: "menu",
    // The menu button and the cell padding, no more.
    width: 40,
    render: (_, row) => {
      const items: MenuProps["items"] = [
        { key: "up", label: "Move up", disabled: row.index === 0 },
        { key: "down", label: "Move down", disabled: row.last },
        { key: "position", label: "Move to position…" },
        { key: "duplicate", label: "Duplicate table" },
        { type: "divider" },
        { key: "remove", label: "Remove table", danger: true },
      ]
      return (
        <Dropdown
          trigger={["click"]}
          menu={{
            items,
            onClick: ({ key }) => args.onAction(row, key as RowAction),
          }}
        >
          <Button
            type="text"
            size="small"
            aria-label={`More actions for ${rowName(row)}`}
            icon={<FontAwesomeIcon icon={faEllipsis} />}
          />
        </Dropdown>
      )
    },
  },
]

/**
 * A cell renders again only for a new row object. The grid builds a row
 * again whenever anything it shows changes and keeps the object otherwise,
 * so selecting a table or editing another renders no cell of this row.
 */
const onNewRow = (a: GridRow, b: GridRow): boolean => a !== b

export const gridColumns = (args: GridColumnArgs): ColumnsType<GridRow> =>
  columns(args).map((column) => ({ ...column, shouldCellUpdate: onNewRow }))

/**
 * The narrowest the grid can be: every column's width, the tick column and
 * the Question column's minimum. The grid scrolls sideways below it, and
 * gives the Question column whatever is left above it.
 */
export const gridMinWidth = (cols: ColumnsType<GridRow>): number =>
  cols.reduce((sum, c) => sum + (typeof c.width === "number" ? c.width : 0), 0) +
  TICK_WIDTH +
  QUESTION_MIN_WIDTH
